import { useEffect, useRef, useState } from 'react';
import { App as AntApp, Button, Input } from 'antd';
import { AsyncButton } from './kit';
import FormSheet from './FormSheet';
import SheetSelect from './SheetSelect';
import * as contactsApi from '../api/contacts';
import { NATIONALITY_OPTIONS, isMalaysian } from '../lib/nationalities';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';

// Quick-create for parties, opened from the "＋ Create new…" row inside the
// vendor/purchaser/co-agency pickers (draft sales + booking forms). Full field
// parity with ContactFormPage / the admin-portal registry forms: name + registration
// no are mandatory (as on the server), everything else optional with the same
// defaults (entity COMPANY, nationality Malaysian). On success the created contact
// is handed back via onCreated so the caller can auto-select it.
//
// Edit mode (`record` = the contact as GET /{type}/:id returns it): the booking form's
// "Edit details" on a party it already names (owner 2026-11-02, Leon Lim). Same
// fields, prefilled, saved with PUT; bank accounts are left out — they are changed
// from Contacts, and only by whoever registered the contact. The saved contact goes
// back through onCreated so the form can refresh its own copy of the name and number.
// `noun` names the slot it was opened from ("tenant", "owner"…) for the title.
//
// A full-height sheet rather than a centred Modal: this runs to a dozen fields plus
// repeatable bank rows, and a Modal on a phone left it scrolling inside a 62vh box
// with the keyboard over the bottom half. It also has to sit ABOVE the picker that
// opened it (4900) — a Modal would have gone to AntD's 6000 layer and trapped its
// own dropdowns above every confirm dialog in the app.

const CFG = {
  vendor: {
    noun: 'vendor / landlord', createFn: contactsApi.createVendors, updateFn: contactsApi.updateVendorsById,
    subTypes: [{ label: 'Vendor', value: 'VENDOR' }, { label: 'Landlord', value: 'LANDLORD' }],
    hasEntity: true, hasNationality: true, hasEmail: false, hasBoardReg: false,
  },
  purchaser: {
    noun: 'purchaser / tenant', createFn: contactsApi.createPurchasers, updateFn: contactsApi.updatePurchasersById,
    subTypes: [{ label: 'Purchaser', value: 'PURCHASER' }, { label: 'Tenant', value: 'TENANT' }],
    hasEntity: true, hasNationality: true, hasEmail: false, hasBoardReg: false,
  },
  coagency: {
    noun: 'co-agency', createFn: contactsApi.createCoAgencies, updateFn: contactsApi.updateCoAgenciesById,
    subTypes: [{ label: 'Co-agency', value: 'CO_AGENCY' }, { label: 'Referral', value: 'REFERRAL' }],
    hasEntity: false, hasNationality: false, hasEmail: true, hasBoardReg: true,
  },
};

const isCompany = (e) => !e || e === 'COMPANY';
const regLabel = (entity, nat) => (isCompany(entity) ? 'SSM Registration No' : isMalaysian(nat) ? 'IC Number' : 'Passport Number');
const regHint = (entity, nat) => (isCompany(entity) ? 'e.g. 201901012345' : isMalaysian(nat) ? 'e.g. 900101-14-5678' : 'Passport number');

export default function PartyQuickCreate({ open, type, defaultSub, record = null, noun, onClose, onCreated }) {
  const cfg = CFG[type] || CFG.vendor;
  // Every OPEN gets a fresh key, so the form always starts blank — keying on
  // type alone would show the last vendor's details when you add a second one.
  // The form stays mounted while closed so the sheet can play its exit.
  const [gen, setGen] = useState(0);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) setGen((g) => g + 1);
    wasOpen.current = open;
  }, [open]);

  return (
    <QuickForm
      key={`${gen}:${type}:${defaultSub || ''}:${record?.id || ''}`}
      open={open}
      cfg={cfg}
      defaultSub={defaultSub}
      record={record}
      noun={noun}
      onClose={onClose}
      onCreated={onCreated}
    />
  );
}

function QuickForm({ open, cfg, defaultSub, record, noun, onClose, onCreated }) {
  const { message } = AntApp.useApp();
  const editing = !!record;
  const r0 = record || {};
  const firstSub = [r0.sub_type, defaultSub].find((v) => cfg.subTypes.some((s) => s.value === v)) || cfg.subTypes[0].value;
  const [subType, setSubType] = useState(firstSub);
  const [entity, setEntity] = useState(r0.entity_type || 'COMPANY');
  const [nationality, setNationality] = useState(r0.nationality || 'Malaysian');
  const [name, setName] = useState(r0.name || '');
  const [registrationNo, setRegistrationNo] = useState(r0.registration_no || '');
  const [boardRegNo, setBoardRegNo] = useState(r0.board_register_no || '');
  const [contactPerson, setContactPerson] = useState(r0.contact_person || '');
  const [phone, setPhone] = useState(r0.phone || r0.contact_no || '');
  const [email, setEmail] = useState(r0.email || '');
  const [address, setAddress] = useState(r0.address || '');
  const [banks, setBanks] = useState([]);

  const individual = cfg.hasEntity && entity === 'INDIVIDUAL';

  // Unsaved typing, guarded here rather than by each caller so every page that
  // opens this sheet gets it. The form remounts blank on every open (see the key
  // above), which is what clears it; the wrappers mark only the agent's own edits.
  // In edit mode it is instead whatever differs from the record the sheet opened
  // with — every field the payload sends, so typing a value back is no change.
  const [dirty, setDirty] = useState(false);
  const edit = (set) => (v) => { setDirty(true); set(v); };
  const onText = (set) => (e) => { setDirty(true); set(e.target.value); };
  const snapshot = JSON.stringify([
    subType, cfg.hasEntity ? entity : '', cfg.hasNationality && individual ? nationality : '',
    name.trim(), registrationNo.trim(), cfg.hasBoardReg ? boardRegNo.trim() : '',
    contactPerson.trim(), phone.trim(), cfg.hasEmail ? email.trim() : '', address.trim(),
  ]);
  const [opened] = useState(snapshot);
  const changed = editing ? snapshot !== opened : dirty;
  const slotNoun = noun || cfg.noun;
  // Read in both prompts: "What you typed in this tenant's details has not been
  // saved…" / "You have changes on this tenant's details that are not saved yet."
  const guard = useUnsavedChanges(open && changed, { what: editing ? `this ${slotNoun}'s details` : `this new ${slotNoun}` });
  // Every way out of the sheet — the chevron and Cancel — asks first.
  const close = () => guard.confirmClose(onClose);

  const editBanks = edit(setBanks);
  const addBank = () => editBanks((xs) => [...xs, { bank: '', acct: '', name: '', is_primary: xs.length === 0 }]);
  const patchBank = (i, patch) => editBanks((xs) => xs.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const removeBank = (i) => editBanks((xs) => xs.filter((_, j) => j !== i));
  const setPrimary = (i) => editBanks((xs) => xs.map((b, j) => ({ ...b, is_primary: j === i })));

  const save = async () => {
    if (!name.trim()) { message.warning('Name is required'); return; }
    if (!registrationNo.trim()) { message.warning(`${regLabel(entity, nationality)} is required`); return; }
    const partialBank = banks.some((b) => {
      const filled = [b.bank, b.acct, b.name].filter((v) => v.trim()).length;
      return filled > 0 && filled < 3;
    });
    if (partialBank) { message.warning('Complete or remove the partial bank account'); return; }
    // Nothing changed: no PUT, no "updated" toast — hand the record back as it is, so
    // the caller can still refresh a form copy that has drifted from the contact.
    if (editing && !changed) { onCreated(record); return; }

    const payload = {
      name: name.trim(),
      sub_type: subType,
      registration_no: registrationNo.trim(),
      // The registry column is contact_no — send it under its own name.
      contact_no: phone.trim(),
      contact_person: contactPerson.trim(),
      address: address.trim(),
      ...(cfg.hasEntity ? { entity_type: entity } : {}),
      ...(cfg.hasNationality && individual ? { nationality } : {}),
      ...(cfg.hasEmail ? { email: email.trim() } : {}),
      ...(cfg.hasBoardReg ? { board_register_no: boardRegNo.trim() } : {}),
      // Edit mode never touches bank accounts (see the note at the top).
      ...(editing ? {} : { bank_accounts: banks.filter((b) => b.bank.trim() || b.acct.trim() || b.name.trim()) }),
    };

    const r = editing ? await cfg.updateFn(record.id, payload) : await cfg.createFn(payload);
    const created = r?.data ?? r;
    message.success(`${cfg.subTypes.find((s) => s.value === subType)?.label || 'Contact'} ${editing ? 'updated' : 'created'}`);
    setDirty(false);
    guard.release();
    onCreated(created);
  };

  return (
    <FormSheet
      open={open}
      // Sits above the picker it was launched from (SheetSelect = 4900), so the new
      // sheet rises over the closing one rather than behind it.
      zIndex={4950}
      title={editing ? `Edit ${slotNoun}` : `New ${slotNoun}`}
      // The server carries a name / number change onto every DRAFT form naming the
      // contact; a form out for signing or issued keeps what was signed.
      sub={editing ? 'updates the contact, this form and your other drafts — sent or issued forms keep what was signed' : 'saved to your contacts — reusable across deals & forms'}
      onClose={close}
      footer={(
        <>
          <Button size="large" style={{ height: 46 }} onClick={close}>Cancel</Button>
          <AsyncButton data-tour="quick-create" type="primary" block onClick={save} onError={(e) => message.error(e.message)}>{editing ? 'Save changes' : 'Create'}</AsyncButton>
        </>
      )}
    >
      {/* Type and entity are SheetSelects, not Segmented: they read as the same kind
          of choice as every other picker in the form, and the sheet has room for the
          full label where a two-up segmented control has to abbreviate. Above this
          sheet's own 4950 so the options open in front of it. */}
      <Field label="Type" req>
        <SheetSelect zIndex={4970} title="Type" value={subType} onChange={edit(setSubType)} options={cfg.subTypes} />
      </Field>

      {cfg.hasEntity && (
        <Field label="Company / Individual" req>
          <SheetSelect
            tour="quick-entity"
            zIndex={4970}
            title="Company / Individual"
            value={entity}
            onChange={edit(setEntity)}
            options={[{ label: 'Company', value: 'COMPANY' }, { label: 'Individual', value: 'INDIVIDUAL' }]}
          />
        </Field>
      )}

      <Field label={individual ? 'Full name (as per IC)' : 'Name (as per SSM)'} req>
        <Input data-tour="quick-name" value={name} onChange={onText(setName)} placeholder={individual ? 'Full legal name' : 'Company / agency name'} />
      </Field>

      {individual && (
        <Field label="Nationality" req>
          {/* Above this sheet's own 4950, or the options open behind it. */}
          <SheetSelect zIndex={4970} title="Nationality" value={nationality} onChange={edit(setNationality)} options={NATIONALITY_OPTIONS} showSearch searchPlaceholder="Search nationality…" />
        </Field>
      )}

      <Field label={regLabel(entity, nationality)} req>
        <Input data-tour="quick-reg" value={registrationNo} onChange={onText(setRegistrationNo)} placeholder={regHint(entity, nationality)} />
      </Field>

      {cfg.hasBoardReg && (
        <Field label="Board registration no">
          <Input value={boardRegNo} onChange={onText(setBoardRegNo)} placeholder="e.g. E(1)1234" />
        </Field>
      )}

      <Field label="Contact person">
        <Input value={contactPerson} onChange={onText(setContactPerson)} placeholder="Person in charge" />
      </Field>
      <Field label="Contact No">
        <Input value={phone} onChange={onText(setPhone)} placeholder="+60 12-345 6789" />
      </Field>
      {cfg.hasEmail && (
        <Field label="Email">
          <Input type="email" value={email} onChange={onText(setEmail)} placeholder="name@email.com" />
        </Field>
      )}
      <Field label="Address">
        <Input.TextArea value={address} onChange={onText(setAddress)} rows={2} placeholder="Mailing address" />
      </Field>

      {!editing && (<>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' }}>BANK ACCOUNTS (OPTIONAL)</div>
        <span onClick={addBank} style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: 12.5, fontWeight: 600 }}>+ Add</span>
      </div>
      {banks.map((b, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span onClick={() => setPrimary(i)} style={{ cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: b.is_primary ? 'var(--primary)' : 'var(--muted)' }}>
              {b.is_primary ? '★ Primary' : '☆ Set primary'}
            </span>
            <span onClick={() => removeBank(i)} style={{ cursor: 'pointer', color: 'var(--bad)', fontSize: 12.5 }}>Remove</span>
          </div>
          <Input value={b.bank} onChange={(e) => patchBank(i, { bank: e.target.value })} placeholder="Bank (e.g. Maybank)" />
          <Input value={b.acct} onChange={(e) => patchBank(i, { acct: e.target.value })} placeholder="Account number" />
          <Input value={b.name} onChange={(e) => patchBank(i, { name: e.target.value })} placeholder="Account holder name" />
        </div>
      ))}
      </>)}
    </FormSheet>
  );
}

// Same Field as ContactFormPage — `req` is the red asterisk on mandatory fields.
const Field = ({ label, req, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>
      {req && <span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>}{label}
    </label>
    {children}
  </div>
);
